import { Component, ElementRef, HostListener, ViewChild } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { DeckCardCount, GameType, GetDeckCardCountsResponse } from "@apiserver-rest/ponder-data-client-base";
import { ShowAddCardToDecksModalEvent } from "@ponder-core/events/deck.events";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { GenericErrorComponent } from "@ponder-shared/components/errors/generic-error.component";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { Subscription, take } from "rxjs";
import { AddCardToDeckInputComponent } from "./add-card-to-deck-input.component";
import { AuthService } from "@ponder-core/auth/auth.service";
import { AuthSession } from "@ponder-core/auth/auth-session";

@Component({
    selector: "add-card-to-deck-modal",
    imports: [BusySpinComponent, GenericErrorComponent, AddCardToDeckInputComponent],
    templateUrl: "./add-card-to-deck-modal.component.html"
})
export class AddCardToDeckModalComponent {
    public isVisible: boolean = false;
    public isLoading: boolean = true;
    public loadingSuccess: boolean = false;

    public username?: string;
    public game!: GameType;
    public name!: string;
    public itemId!: string;
    public decks: DeckCardCountTotal[] = [];

    public readonly closeSvgIcon: string = SvgIconMap.get(SvgIcon.Close)!;
    private showModalSub!: Subscription;

    public deckFilter?: string;
    public decksContainingOnly: boolean = false;

    public hasNormal: boolean = true;
    public hasSpecial: boolean = true;

    @ViewChild("dialogContainer") private dialogContainerRef!: ElementRef<HTMLElement>;

    public constructor(private ponderDataClient: PonderDataClient, private events: EventBroker, private auth: AuthService) {
        this.showModalSub = this.events.deck.showAddCardToDecksModalEvent.subscribe({
            next: (event: ShowAddCardToDecksModalEvent) => {
                this.onOpen(event.game, event.name, event.itemId, event.hasNormal, event.hasSpecial);
            }
        });
    }

    public ngOnInit(): void {
        this.auth.getSessionAsync().then((authSession?: AuthSession) => {
            this.username = authSession?.username;
        })
    }

    public ngOnDestroy(): void {
        this.showModalSub.unsubscribe();
    }

    // -------------------------------------------------------------------------------------
    // Global Events
    // -------------------------------------------------------------------------------------

    private mouseDownOutside: boolean = false;

    @HostListener("document:keydown.escape", ["$event"]) 
    private onEscapePressed(event: KeyboardEvent): void {
        this.onClose();
    }

    @HostListener("document:mousedown", ["$event"])
    private onGlobalMouseDown(event: MouseEvent): void {
        if (this.isVisible && !this.dialogContainerRef.nativeElement.contains(event.target as HTMLInputElement)) {
            this.mouseDownOutside = true;
        }
    }

    @HostListener("document:mouseup", ["$event"])
    private onGlobalMouseUp(event: MouseEvent): void {
        // Did the user click outside of our modal panel?
        if (this.isVisible && this.mouseDownOutside && !this.dialogContainerRef.nativeElement.contains((event.target as HTMLInputElement))) {
            this.onClose();
        }

        this.mouseDownOutside = false;
    }

    // -------------------------------------------------------------------------------------
    // Actions
    // -------------------------------------------------------------------------------------

    public onOpen(game: GameType, name: string, itemId: string, hasNormal: boolean, hasSpecial: boolean): void {
        if ((game != this.game) || (itemId != this.itemId)) {
            this.game = game;
            this.name = name;
            this.itemId = itemId;
            this.isLoading = false;
            this.loadingSuccess = false;
            this.decks = [];

            this.fetchDeckData();
        }

        this.hasNormal = hasNormal;
        this.hasSpecial = hasSpecial;
        this.deckFilter = undefined;
        this.isVisible = true;
    }

    public onClose(): void {
        this.isVisible = false;
    }

    private fetchDeckData(): void {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.loadingSuccess = false;

        this.ponderDataClient
            .decksWithCardAll(GameType.Magic, this.itemId)
            .pipe(take(1))
            .subscribe({
                next: (response: GetDeckCardCountsResponse) => {
                    if (response.success) {
                        this.loadingSuccess = true;
                        this.decks = [];

                        response.decks?.forEach(d => {
                            this.decks.push({
                                deck: d,
                                total: ((d.mainboardCount ?? 0) + (d.mainboardSpecialCount ?? 0) + 
                                        (d.sideboardCount ?? 0) + (d.sideboardSpecialCount ?? 0) + 
                                        (d.maybeboardCount ?? 0) + (d.maybeboardSpecialCount ?? 0) + 
                                        (d.planningCount ?? 0) + (d.planningSpecialCount ?? 0))
                            });
                        })
                        
                        this.sortDecks();
                    }
                }
            })
            .add(() => { this.isLoading = false; });
    }

    private sortDecks(): void {
        this.decks?.sort((a, b) => {
            if (a.deck.isFavorited && !b.deck.isFavorited) {
                return -1;
            } else if (b.deck.isFavorited && !a.deck.isFavorited) {
                return 1;
            } else {
                return a.deck.deckName!.localeCompare(b.deck.deckName!);
            }
        });
    }

    public isDeckVisible(deck: DeckCardCountTotal): boolean {
        if (this.decksContainingOnly && !deck.total) {
            return false;
        }
        
        if (!this.deckFilter) {
            return true;
        } else {
            return deck.deck.deckName!.toLowerCase().indexOf(this.deckFilter) >= 0 ? true : false;
        }
    }

    public onFilterUpdated(input: HTMLInputElement): void {
        this.deckFilter = input.value.toLowerCase();
    }

    public onCheckboxToggle(checkbox: HTMLInputElement): void {
        this.decksContainingOnly = checkbox.checked;
    }
}

export type DeckCardCountTotal = {
    deck: DeckCardCount;
    total: number;
}